import {
  Home,
  CreditCard,
  Settings,
  ShieldCheck,
  Building2,
  Grid,
  Key,
  Shield,
  Users,
  Layers,
  SlidersHorizontal,
} from 'lucide-react'
import type { NavigationItem, ExpandableNavigationItem } from '@airclou/shell'

export type TenantRole = 'owner' | 'admin' | 'billing_admin' | 'viewer'

/**
 * Can this role change org settings, membership and structure?
 * Mirrors `TENANT_ROLE_SETS.MANAGE`.
 */
export function canManageOrg(role: TenantRole | undefined): boolean {
  return role === 'owner' || role === 'admin'
}

/**
 * Can this role change the plan? Mirrors `TENANT_ROLE_SETS.BILLING`.
 *
 * Exported so the Overview page's billing calls-to-action can hide on the same
 * rule as the sidebar entry — a shortcut to a page the sidebar hides is the
 * same dead end, one click further in.
 */
export function canSeeBilling(role: TenantRole | undefined): boolean {
  return canManageOrg(role) || role === 'billing_admin'
}

/**
 * Sidebar information architecture.
 *
 * Two groups, split by what you go there to do:
 *   - flat items = the places you work (the org's structure, its apps, its
 *     plan, and the audit trail — a log you read, not a setting you change)
 *   - "Settings" group = the places you configure
 *
 * The group is named for what it holds. It used to be called "Organization"
 * while holding billing/SSO/security/audit, with Companies and People — the
 * things that actually make up the organization — sitting outside it. The word
 * survives one level down, on the single page it always described (`/settings`).
 *
 * Visibility follows what a role can **change**, not what it can read. The sets
 * match the backend's `TENANT_ROLE_SETS`
 * (`backend/product/middleware/statusAuth.ts`, documented in
 * `docs/role-based-authorization-plan.md` §5), which gate writes: MANAGE
 * (owner, admin) for org settings, BILLING (owner, admin, billing_admin) for
 * the plan. `billing_admin` is a *side* role for plan and billing, not a rank
 * above `admin`: it sees Billing and must NOT see the settings group.
 *
 * So a hidden entry is not an inaccessible one. Reads in this app are confined
 * to the caller's tenant but otherwise ungated by design (§5, "reads are
 * already tenant-confined and unchanged"), so a viewer who types /settings or
 * /security still gets the page, and should — the point of hiding is to keep
 * the sidebar to what that role came here to do, not to enforce anything. The
 * backend gates remain the only enforcement point.
 */
export function buildNavigation(
  role: TenantRole | undefined,
  t: (key: string) => string,
): { mainNavigation: NavigationItem[]; expandableItems: ExpandableNavigationItem[] } {
  const manageOrg = canManageOrg(role)
  const seeBilling = canSeeBilling(role)

  const mainNavigation: NavigationItem[] = [
    { name: t('nav.overview'), href: '/', icon: Home },
    { name: t('nav.companies'), href: '/companies', icon: Building2 },
    { name: t('nav.workspaces'), href: '/workspaces', icon: Layers },
    { name: t('nav.people'), href: '/people', icon: Users },
    { name: t('nav.apps'), href: '/apps', icon: Grid },
    // Billing is top-level: it is what this console is primarily for, and it is
    // where the Overview page's plan and usage shortcuts land.
    ...(seeBilling
      ? [{ name: t('nav.billing'), href: '/billing', icon: CreditCard }]
      : []),
    // Audit Logs is flat and ungated: it is a log you read, not a setting you
    // change. In the settings group it sat behind that group's write-capability
    // gate, which hid it from `viewer` and `billing_admin` — who can read it,
    // and whose own actions are in it. Overview's activity feed already sends
    // every role here from "View all", so the sidebar was the only surface
    // treating the page as admin-only.
    { name: t('nav.auditLogs'), href: '/audit', icon: ShieldCheck },
  ]

  const expandableItems: ExpandableNavigationItem[] = manageOrg
    ? [
        {
          name: t('nav.settings'),
          icon: Settings,
          children: [
            // Labeled for the page it opens, whose heading is "Organization".
            // A generic "General" here would have the label and the page
            // disagree at the moment of arrival.
            { name: t('nav.organization'), href: '/settings', icon: SlidersHorizontal },
            { name: t('nav.sso'), href: '/sso', icon: Key },
            { name: t('nav.security'), href: '/security', icon: Shield },
          ],
        },
      ]
    : []

  return { mainNavigation, expandableItems }
}
